import { ReactElement } from 'react'

export type VideosArgs = {
  course?: number | string
  direction?: 'asc' | 'desc'
  page?: number
  pageSize?: number
  playlist?: string
  search?: string
  sortByReaction?: string
}

export type VideosResponse = {
  totalCount: number
  items: Video[]
}

export type Comment = {
  avatar: string
  created: string
  firstName: string
  id: string
  lastName: string
  likes: Like[]
  studentId: number
  text: string
  updated: string
}

export type Like = {
  commentId: string
  created: string
  firstName: string
  id: string
  lastName: string
  studentId: number
  updated: string
}

export type Video = {
  allUnderstandingCount: number
  code: string
  description: string
  failedPresentationCount: number
  firePresentationCount: number
  hardUnderstandingCount: number
  hosting: Hosting
  hostingHash: string
  hostingId: string
  id?: string
  improvedUnderstandingCount: number
  mentor: string
  mentorId: number
  name: string
  normalCount: number
  nothingNewCount: number
  obsoleteCount: number
  order: number
  settings: Setting[]
  startDate: string
  superContentCount: number
  tags: Tag[]
  url: string
  hasMainMdxNote: boolean
  slug?: string
  publicVisibility?: PublicVisibility
  seoDescription?: string
  hasSimulators: boolean
}

export type GetVideoByIdRes = {
  comments: Comment[]
  description: string
  hosting: Hosting
  hostingHash: string
  hostingId: string
  id: string
  name: string
  reactions: ReactionType[]
  settings: Setting[]
  tags: Tag[]
  url: string
  mdxNotes: AdminMdxNoteResponseDto[]
  videoSimulators: VideoSimulator[] | null
}

export type VideoSimulator = {
  simulatorSlug: string
  titleSnapshot: string
}

export type AdminMdxNoteResponseDto = {
  publicVisibility: PublicVisibility
  type: NoteType
  id: string
  mdxFilePath: string
}

export type Hosting = 'vimeo' | 'vk' | 'youtube'

export type Setting = {
  accessLevel: number
  courseId: number
  forLesson: number
  id?: string
  maxStudentStartCourseDate: number
  minStudentStartCourseDate: number
  playlistId: string
}

export type Tag = {
  id?: string
  text: string
}

export type ReactionType = {
  id: string
  reaction: string
  studentId: number
}

type Sorting = {
  sortByReaction: string
}

export type VideosQueries = {
  filters: VideosArgs
  sorting: Sorting
}

export type ChangeVideoOrderBody = {
  courseId: number | string
  order: number
  playlistId: string
  videoId: string
}

export enum SortComments {
  LikesAsc,
  LikesDesc,
  DateAsc,
  DateDesc,
}

export type ReactionsData = {
  countName: Reactions
  icon: ReactElement
  reaction: Reactions
  title: string
  variant: 'primary' | 'secondary'
}

export enum Reactions {
  AllUnderstanding = 'all_understanding',
  FailedPresentation = 'failed_presentation',
  FirePresentation = 'fire_presentation',
  HardUnderstanding = 'hard_understanding',
  ImprovedUnderstanding = 'improved_understanding',
  Normal = 'normal',
  NothingNew = 'nothing_new',
  Obsolete = 'obsolete',
  SuperContent = 'super_content',
}

export type VideoSettingsState = {
  [key: string]: Setting[]
}

export type CourseBlockState = {
  id: string
  courseId: number | null
  selectedPlaylistsIds: string[]
  tabValue: boolean | string
}

export type FileOrDirectory = {
  children?: FileOrDirectory[]
  name: string
  path: string
  type: EntryType
}

export enum EntryType {
  Directory = 'directory',
  File = 'file',
}

// publicVisibility must be provided if NoteType.MAIN and must NOT be provided if NoteType.PREVIEW
export type AddMDXNoteModel =
  | {
      mdxFilePath: string
      type: NoteType.Main
      publicVisibility: PublicVisibility
    }
  | {
      mdxFilePath: string
      type: NoteType.Preview
      publicVisibility?: never
    }

export enum PublicVisibility {
  None = 'NONE',
  Public = 'PUBLIC',
  Registered = 'REGISTERED',
  Subscriber = 'SUBSCRIBER',
}

export enum NoteType {
  Main = 'MAIN',
  Preview = 'PREVIEW',
}

export type UpdateMainMDXNoteModel = Omit<AddMDXNoteModel, 'type'>

export type UpdatePreviewMDXNoteModel = Pick<AddMDXNoteModel, 'mdxFilePath'>

export type MDXParsedResponseDto = {
  /* Скомпилированный JS-код MDX*/
  code: string
  /* Список ошибок при бандлинге MDX */
  errors: Array<{}>
  /* Frontmatter (метаданные) MDX-файла */
  frontmatter: any
  /* Table of Contents MDX-файла */
  toc: {
    map: any
  }
}
